import React, { useEffect, useState } from "react";
import "./UserView.css";
import "../tables/Button.css";
import "../SafetyObservation/safetyObservation.css";
import "../SafetyMeetings/safetyMeetings.css";
import { useNavigate, useParams } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";
import { IoMdArrowBack } from "react-icons/io";
import { FiDownload, FiEye, FiTrash2 } from "react-icons/fi";
import API_URL from "../../src/Config";
import {
  downloadSafetyFile,
  formatDateDisplay,
  openSafetyFile,
  parseApiError,
} from "../SafetyObservation/observationUtils";
import {
  getMeetingAttachmentName,
  getMeetingAttachmentSizeLabel,
} from "../SafetyMeetings/meetingUtils";

function SafetyMeetingsView({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const { meetingId } = useParams();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [isDeletingAttachment, setIsDeletingAttachment] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const fetchMeeting = async () => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");

      const response = await axios.get(
        `${API_URL}/api/safety/meetings/${meetingId}`,
        {
          headers: {
            Authorization: authKey,
          },
        },
      );

      setMeeting(response.data);
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to fetch meeting"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeeting();
  }, [meetingId]);

  const handleDownload = async (attachment) => {
    try {
      await downloadSafetyFile(attachment.id, attachment.filename);
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to download file"), "error");
    }
  };

  const handleViewFile = async (attachment) => {
    try {
      await openSafetyFile(attachment.id);
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to open file"), "error");
    }
  };

  const handleDeleteMeeting = async () => {
    try {
      setIsDeleting(true);
      const authKey = localStorage.getItem("authKey");

      const response = await axios.delete(
        `${API_URL}/api/safety/meetings/${meetingId}`,
        {
          headers: {
            Authorization: authKey,
          },
        },
      );

      showSnackbar(response.data?.message || "Meeting deleted successfully");
      setShowDeleteConfirm(false);
      window.setTimeout(() => {
        navigate("/safety-meetings");
      }, 700);
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to delete meeting"), "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteAttachment = async () => {
    if (!attachmentToDelete) return;

    try {
      setIsDeletingAttachment(true);
      const authKey = localStorage.getItem("authKey");

      const response = await axios.delete(
        `${API_URL}/api/safety/meetings/attachments/${attachmentToDelete.id}`,
        {
          headers: {
            Authorization: authKey,
          },
        },
      );

      showSnackbar(response.data?.message || "Attachment deleted successfully");
      setAttachmentToDelete(null);
      await fetchMeeting();
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to delete attachment"), "error");
    } finally {
      setIsDeletingAttachment(false);
    }
  };

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content observation-view-page">
          <div className="data-list-header">
            <div className="data-list-header-content">
              <div className="data-list-heading-content-left-side">
                <Tooltip title="Back" arrow>
                  <IoMdArrowBack
                    className="back-company"
                    onClick={() => navigate("/safety-meetings")}
                  />
                </Tooltip>
                Meeting View
              </div>
              {/* <div className="observation-view-actions">
                <button
                  className="button-bg"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete
                </button>
                <button
                  className="button-bg"
                  onClick={() => navigate(`/edit-safety-meeting/${meetingId}`)}
                >
                  Edit
                </button>
              </div> */}
            </div>
          </div>

          <div className="header-separator"></div>

          {loading ? (
            <div className="loader-container">
              <div className="loader"></div>
              <div>Loading...</div>
            </div>
          ) : meeting ? (
            <>
              <div className="observation-section-card">
                <div className="observation-section-title">Meeting Details</div>
                <div className="observation-section-body">
                  <div className="observation-detail-grid">
                    <div className="observation-detail-item">
                      <span className="observation-detail-label">Title</span>
                      <span>{meeting.title || "NA"}</span>
                    </div>
                    <div className="observation-detail-item">
                      <span className="observation-detail-label">Date</span>
                      <span>{formatDateDisplay(meeting.date)}</span>
                    </div>
                    <div className="observation-detail-item">
                      <span className="observation-detail-label">Location</span>
                      <span>{meeting.location || "NA"}</span>
                    </div>
                    <div className="observation-detail-item">
                      <span className="observation-detail-label">
                        Created By
                      </span>
                      <span>{meeting.created_by_name || "NA"}</span>
                    </div>
                    <div className="observation-detail-item form-group-full-width">
                      <span className="observation-detail-label">Agenda</span>
                      <span className="meeting-detail-text">
                        {meeting.agenda || "NA"}
                      </span>
                    </div>
                    <div className="observation-detail-item form-group-full-width">
                      <span className="observation-detail-label">Minutes</span>
                      <span className="meeting-detail-text">
                        {meeting.minutes || "NA"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="observation-section-card">
                <div className="observation-section-title">Attendees</div>
                <div className="observation-section-body">
                  {Array.isArray(meeting.attendees) && meeting.attendees.length > 0 ? (
                    <div className="meeting-attendee-list">
                      {meeting.attendees.map((attendee) => (
                        <span
                          key={attendee.user_id || attendee.name}
                          className="meeting-attendee-chip"
                        >
                          {attendee.name || "NA"}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="observation-empty-state">
                      No attendees added.
                    </div>
                  )}
                </div>
              </div>

              <div className="observation-section-card">
                <div className="observation-section-title">Documents</div>
                <div className="observation-section-body">
                  {Array.isArray(meeting.attachments) &&
                  meeting.attachments.length > 0 ? (
                    <div className="observation-attachments">
                      {meeting.attachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className="observation-attachment-row"
                        >
                          <div className="observation-attachment-meta">
                            <div className="observation-attachment-name">
                              {getMeetingAttachmentName(attachment)}
                            </div>
                            <div className="observation-attachment-subtext">
                              {attachment.file_type || "NA"} |{" "}
                              {getMeetingAttachmentSizeLabel(attachment)}
                            </div>
                          </div>
                          <div className="observation-attachment-actions">
                            <Tooltip title="View" arrow>
                              <button
                                className="observation-icon-button"
                                type="button"
                                onClick={() => handleViewFile(attachment)}
                              >
                                <FiEye />
                              </button>
                            </Tooltip>
                            <Tooltip title="Download" arrow>
                              <button
                                className="observation-icon-button"
                                type="button"
                                onClick={() => handleDownload(attachment)}
                              >
                                <FiDownload />
                              </button>
                            </Tooltip>
                            {/* <Tooltip title="Delete" arrow>
                              <button
                                className="observation-icon-button observation-icon-button-danger"
                                type="button"
                                onClick={() => setAttachmentToDelete(attachment)}
                              >
                                <FiTrash2 />
                              </button>
                            </Tooltip> */}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="observation-empty-state">
                      No documents uploaded.
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="no-users-container">
              <div className="no-users-message">Meeting not found</div>
            </div>
          )}
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="confirm-delete-popup">
          <div className="confirm-delete-popup-content">
            <div className="confirm-model">
              <div className="confirm-model-content">
                <div className="confirm-model-content-body">
                  <p>
                    Are you sure you want to delete meeting{" "}
                    <strong>{meeting?.title || "this item"}</strong>?
                  </p>
                </div>
                <div className="confirm-model-content-footer">
                  <button
                    className="confirm-delete-button"
                    onClick={handleDeleteMeeting}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Deleting..." : "Yes"}
                  </button>
                  <button
                    className="confirm-cancel-button"
                    onClick={() => !isDeleting && setShowDeleteConfirm(false)}
                    disabled={isDeleting}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {attachmentToDelete && (
        <div className="confirm-delete-popup">
          <div className="confirm-delete-popup-content">
            <div className="confirm-model">
              <div className="confirm-model-content">
                <div className="confirm-model-content-body">
                  <p>
                    Are you sure you want to delete attachment{" "}
                    <strong>{attachmentToDelete.filename || "this file"}</strong>?
                  </p>
                </div>
                <div className="confirm-model-content-footer">
                  <button
                    className="confirm-delete-button"
                    onClick={handleDeleteAttachment}
                    disabled={isDeletingAttachment}
                  >
                    {isDeletingAttachment ? "Deleting..." : "Yes"}
                  </button>
                  <button
                    className="confirm-cancel-button"
                    onClick={() =>
                      !isDeletingAttachment && setAttachmentToDelete(null)
                    }
                    disabled={isDeletingAttachment}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default SafetyMeetingsView;
